import classNames from 'classnames';
import { AriaAttributes, CSSProperties, ReactNode, forwardRef } from 'react';

import useIsSafari from '../../hooks/useIsSafari';

import limitWords from './helpers/limitWords';

import styles from './Text.module.css';

type TextProps = {
    children?: ReactNode;
    className?: string;
    subdued?: boolean;
    bold?: boolean;
    strikethrough?: boolean;
    underline?: boolean;
    color?:
        | 'default'
        | 'brand-contrast'
        | 'brand'
        | 'error'
        | 'success'
        | 'disabled';
    newlines?: boolean;
    id?: string;
    flex?: boolean | number;
    size?: 'xsmall' | 'small' | 'default' | 'large' | 'xlarge' | 'xxlarge';
    lineHeight?: 'small' | 'default' | 'large';
    align?: 'left' | 'center' | 'right';
    wordBreak?: 'normal' | 'break-all' | 'break-word';
    tag?: 'p' | 'div' | 'span' | 'h1' | 'h2' | 'h3' | 'h4';
    maxWords?: number;
    widthAutoSize?: boolean;
    hideOverflow?: boolean;
    monospace?: boolean;
    noWrapping?: boolean;
    inlineBlock?: boolean;
    transform?: CSSProperties['textTransform'];
} & AriaAttributes;

const Text = forwardRef<HTMLElement, TextProps>(
    (
        {
            children,
            subdued,
            bold,
            strikethrough,
            underline,
            newlines,
            className,
            id,
            maxWords,
            align,
            size,
            lineHeight = 'default',
            wordBreak = 'break-word',
            tag = 'span',
            color,
            flex = false,
            hideOverflow = false,
            monospace = false,
            noWrapping = false,
            inlineBlock = false,
            widthAutoSize = false,
            transform,
            ...ariaAttributes
        },
        ref
    ) => {
        const isSafari = useIsSafari();
        const classes = classNames(className, styles.Root, {
            [styles.subdued]: subdued,
            [styles.bold]: bold,
            [styles.strikethrough]: strikethrough,
            [styles.underline]: underline,
            [styles.newlines]: newlines,
            [styles.inlineBlock]: inlineBlock,
            [styles.lineHeightSmall]: lineHeight === 'small',
            [styles.lineHeightDefault]: lineHeight === 'default',
            [styles.lineHeightLarge]: lineHeight === 'large',
            [styles.sizeXSmall]: size === 'xsmall',
            [styles.sizeSmall]: size === 'small',
            [styles.sizeDefault]: size === 'default',
            [styles.sizeLarge]: size === 'large',
            [styles.sizeXLarge]: size === 'xlarge',
            [styles.sizeXXLarge]: size === 'xxlarge',
            [styles.alignLeft]: align === 'left',
            [styles.alignRight]: align === 'right',
            [styles.alignCenter]: align === 'center',
            [styles.widthAutoSize]: widthAutoSize,
            [styles.wordBreakAll]: wordBreak === 'break-all',
            [styles.wordBreakWord]: wordBreak === 'break-word',
            [styles.colorDefault]: color === 'default',
            [styles.colorBrandContrast]: color === 'brand-contrast',
            [styles.colorBrand]: color === 'brand',
            [styles.colorSuccess]: color === 'success',
            [styles.colorError]: color === 'error',
            [styles.colorDisabled]: color === 'disabled',
            [styles.hideOverflow]: hideOverflow,
            [styles.noWrapping]: hideOverflow || noWrapping,
            [styles.monospace]: monospace,
            [styles.forceSafariRerender]: isSafari,
        });

        const style: CSSProperties = {};
        if (flex) {
            style.flex = flex === true ? 1 : flex;
        }

        if (transform) {
            style.textTransform = transform;
        }

        const Tag = tag;

        return (
            <Tag
                className={classes}
                style={style}
                id={id}
                {...ariaAttributes}
                // @ts-ignore
                ref={ref}
            >
                {typeof children === 'string' && maxWords
                    ? limitWords(children, maxWords)
                    : children}
            </Tag>
        );
    }
);

export default Text;
